import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { clamp, fitRange, timeWindow, zoomWindow, type TimeWindow } from './timeline-scale';

export function useTimelineViewport(last: number, frame: number, playing: boolean) {
  const [view, setView] = useState<TimeWindow>({ start: 0, span: last });
  const [width, setWidth] = useState(800);
  const host = useRef<HTMLDivElement>(null);
  const follow = useRef(true);
  const current = useRef(view); current.current = view;
  const set = useCallback((next: TimeWindow) => { current.current = next; setView(next); }, []);
  useEffect(() => { set(timeWindow(0, last, last)); }, [last, set]);
  useLayoutEffect(() => {
    const element = host.current; if (!element) return;
    const measure = () => setWidth(Math.max(1, element.clientWidth - 32));
    measure();
    if (typeof ResizeObserver === 'undefined') { window.addEventListener('resize', measure); return () => window.removeEventListener('resize', measure); }
    const observer = new ResizeObserver(measure); observer.observe(element);
    return () => observer.disconnect();
  }, []);
  useEffect(() => { if (playing) follow.current = true; }, [playing]);
  useEffect(() => {
    const value = current.current;
    if (playing && follow.current && (frame < value.start || frame > value.start + value.span))
      set(timeWindow(frame - value.span * .1, value.span, last));
  }, [frame, playing, last, set]);
  const pan = (start: number) => { follow.current = false; set(timeWindow(start, current.current.span, last)); };
  const zoom = (factor: number, anchor = frame, fraction = .5) => set(zoomWindow(current.current, factor, anchor, fraction, last));
  const go = () => { follow.current = true; set(timeWindow(frame - current.current.span / 2, current.current.span, last)); };
  const fit = () => { follow.current = true; set({ start: 0, span: last }); };
  const fitSelection = (first: number, end: number) => { follow.current = true; set(fitRange(first, end, last)); };
  return { view, current, width, host, set, pan, zoom, go, fit, fitSelection,
    maximum: Math.min(29, last), zoomFactor: last / clamp(view.span, 1, last) };
}
